import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSend, FiInfo, FiAlertCircle, FiCopy, FiFileText, FiShield, FiPaperclip, FiHelpCircle, FiChevronDown } from 'react-icons/fi';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Alert } from '../../components/ui/Alert';
import { Spinner } from '../../components/ui/Spinner';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { PainelResultado } from '../../components/layout/PainelResultado';
import { AnexoUpload } from '../../components/AnexoUpload';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { getEstabelecimentoAtualId } from '../../lib/estabelecimento';
import {
  esicPortalService, TIPO_PEDIDO_LABEL, FORMA_RECEBIMENTO_LABEL,
  type TipoPedidoEsic, type FormaRecebimento, type AssuntoEsic,
} from '../../services/esic.service';
import { solicitacoesService } from '../../services/solicitacoes.service';

const EXT_ANEXOS = ['pdf', 'jpg', 'jpeg', 'png', 'webp', 'doc', 'docx', 'xls', 'xlsx', 'txt'];
const MAX_ANEXOS = 5;

export function EsicAbrirPage() {
  const { cidadao, cadastroCompleto } = useAuth();
  const navigate = useNavigate();

  const [tipoPedido, setTipoPedido] = useState<TipoPedidoEsic>('informacao');
  const [formaRecebimento, setFormaRecebimento] = useState<FormaRecebimento>('plataforma');
  const [assuntoId, setAssuntoId] = useState('');
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [justificativa, setJustificativa] = useState('');
  const [anexos, setAnexos] = useState<File[]>([]);

  // FAQ do SIC (item 2.54) — perguntas frequentes cadastradas pela prefeitura.
  const [faq, setFaq] = useState<Array<{ id: string; pergunta: string; resposta: string }>>([]);
  const [mostrarFaq, setMostrarFaq] = useState(false);
  const [faqAberto, setFaqAberto] = useState<string | null>(null);

  const [assuntos, setAssuntos] = useState<AssuntoEsic[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [resultado, setResultado] = useState<{ numero: string; hash_publico: string } | null>(null);

  useEffect(() => {
    esicPortalService.listarAssuntos(getEstabelecimentoAtualId())
      .then((a) => {
        setAssuntos(a);
        if (a.length > 0) setAssuntoId(a[0].id);
      })
      .finally(() => setCarregando(false));
  }, []);

  useEffect(() => {
    supabase.from('faq_sic')
      .select('id, pergunta, resposta')
      .eq('estabelecimento_id', getEstabelecimentoAtualId())
      .eq('ativo', true)
      .order('ordem')
      .then(({ data }) => setFaq((data ?? []) as Array<{ id: string; pergunta: string; resposta: string }>));
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!cidadao) return;
    if (titulo.trim().length < 3) { setErro('Informe um título.'); return; }
    if (descricao.trim().length < 10) { setErro('Detalhe seu pedido (mínimo 10 caracteres).'); return; }
    if (!assuntoId) { setErro('Selecione um assunto.'); return; }

    setErro(null);
    setEnviando(true);
    try {
      const r = await esicPortalService.abrir({
        estabelecimento_id: getEstabelecimentoAtualId(),
        cidadao_id: cidadao.id,
        assunto_id: assuntoId,
        tipo_pedido: tipoPedido,
        forma_recebimento: formaRecebimento,
        titulo: titulo.trim(),
        descricao: descricao.trim(),
        justificativa: justificativa.trim() || undefined,
      });
      // Anexos diversos.
      for (const arquivo of anexos) {
        await solicitacoesService.uploadAnexo({
          documentoId: r.id,
          estabelecimentoId: getEstabelecimentoAtualId(),
          cidadaoId: cidadao.id,
          cidadaoNome: cidadao.nome,
          arquivo,
        });
      }
      setResultado({ numero: r.numero, hash_publico: r.hash_publico });
      // E-mail de confirmação de abertura (best-effort).
      try { await supabase.functions.invoke('notificar-cidadao-protocolo', { body: { documento_id: r.id, evento: 'criado' } }); } catch { /* não bloqueia */ }
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Falha ao enviar.');
    } finally {
      setEnviando(false);
    }
  }

  if (resultado) {
    return <PainelSucesso numero={resultado.numero} hash={resultado.hash_publico} onVer={() => navigate(`/solicitacao/${resultado.numero}`)} />;
  }

  if (!cadastroCompleto) {
    return (
      <PortalPage
        titulo="Pedido de acesso à informação"
        subtitulo="Sistema Eletrônico do Serviço de Informação ao Cidadão (e-SIC) — Lei 12.527/2011 (LAI)."
        icone={<FiInfo size={26} />}
        voltarHref="/"
      >
        <SectionCard titulo="Complete seu cadastro" icone={<FiShield size={18} />}>
          <Alert variante="aviso">
            Para abrir um pedido de acesso à informação você precisa completar seu cadastro
            (CPF, telefone e endereço). A Lei 12.527/2011 exige identificação do solicitante.
          </Alert>
          <div className="flex justify-end mt-4">
            <Button onClick={() => navigate('/completar-cadastro')}>Completar cadastro</Button>
          </div>
        </SectionCard>
      </PortalPage>
    );
  }

  return (
    <PortalPage
      titulo="Pedido de acesso à informação"
      subtitulo="Sistema Eletrônico do Serviço de Informação ao Cidadão (e-SIC) — Lei 12.527/2011 (LAI)."
      icone={<FiInfo size={26} />}
      voltarHref="/"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {erro && <Alert variante="erro">{erro}</Alert>}

        <Alert variante="info">
          <strong>Você tem direito a esta informação.</strong> A Administração Pública tem 20 dias úteis
          para responder, prorrogáveis por mais 10 com justificativa. Em caso de negativa, você pode
          recorrer em até 4 instâncias.
        </Alert>

        {/* FAQ do SIC (item 2.54) */}
        {faq.length > 0 && (
          <div className="rounded-xl border border-slate-200 bg-white">
            <button
              type="button"
              onClick={() => setMostrarFaq((v) => !v)}
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                <FiHelpCircle className="text-institutional" size={18} />
                Perguntas frequentes (FAQ do SIC)
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">{faq.length}</span>
              </span>
              <FiChevronDown size={18} className={`text-slate-400 transition ${mostrarFaq ? 'rotate-180' : ''}`} />
            </button>
            {mostrarFaq && (
              <ul className="divide-y divide-slate-100 border-t border-slate-100">
                {faq.map((f) => {
                  const aberto = faqAberto === f.id;
                  return (
                    <li key={f.id}>
                      <button
                        type="button"
                        onClick={() => setFaqAberto(aberto ? null : f.id)}
                        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50"
                      >
                        <span className="text-sm font-medium text-slate-800">{f.pergunta}</span>
                        <FiChevronDown size={16} className={`flex-shrink-0 text-slate-400 transition ${aberto ? 'rotate-180' : ''}`} />
                      </button>
                      {aberto && (
                        <p className="whitespace-pre-wrap px-4 pb-4 text-sm text-slate-600">{f.resposta}</p>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        <SectionCard titulo="Sobre seu pedido" icone={<FiFileText size={18} />}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
            <div>
              <Label obrigatorio>Tipo do pedido</Label>
              <select
                value={tipoPedido}
                onChange={(e) => setTipoPedido(e.target.value as TipoPedidoEsic)}
                required
                className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:border-institutional focus:ring-2 focus:ring-institutional"
              >
                {(Object.keys(TIPO_PEDIDO_LABEL) as TipoPedidoEsic[]).map((t) => (
                  <option key={t} value={t}>{TIPO_PEDIDO_LABEL[t]}</option>
                ))}
              </select>
            </div>
            <div>
              <Label obrigatorio>Como deseja receber a resposta?</Label>
              <select
                value={formaRecebimento}
                onChange={(e) => setFormaRecebimento(e.target.value as FormaRecebimento)}
                required
                className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:border-institutional focus:ring-2 focus:ring-institutional"
              >
                {(Object.keys(FORMA_RECEBIMENTO_LABEL) as FormaRecebimento[]).map((f) => (
                  <option key={f} value={f}>{FORMA_RECEBIMENTO_LABEL[f]}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <Label obrigatorio>Assunto</Label>
              {carregando ? (
                <div className="h-10 flex items-center"><Spinner tamanho="sm" /></div>
              ) : assuntos.length === 0 ? (
                <p className="text-xs text-amber-700 mt-1">
                  Nenhum assunto do e-SIC configurado pela prefeitura.
                </p>
              ) : (
                <select
                  value={assuntoId}
                  onChange={(e) => setAssuntoId(e.target.value)}
                  required
                  className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:border-institutional focus:ring-2 focus:ring-institutional"
                >
                  {assuntos.map((a) => (
                    <option key={a.id} value={a.id}>{a.nome}</option>
                  ))}
                </select>
              )}
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="titulo" obrigatorio>Título do pedido</Label>
              <Input
                id="titulo"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                required
                placeholder="Resuma sua solicitação em uma frase"
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="desc" obrigatorio>Descrição detalhada</Label>
              <textarea
                id="desc"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                rows={6}
                required
                placeholder="Descreva com detalhes a informação ou documento que você deseja receber..."
                className="w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:border-institutional focus:ring-2 focus:ring-institutional resize-y"
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="just">Justificativa (opcional)</Label>
              <textarea
                id="just"
                value={justificativa}
                onChange={(e) => setJustificativa(e.target.value)}
                rows={3}
                placeholder="Você não é obrigado a justificar. Por que está solicitando esta informação? (opcional)"
                className="w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:border-institutional focus:ring-2 focus:ring-institutional resize-y"
              />
              <p className="text-xs text-amber-700 mt-1 flex items-start gap-1">
                <FiInfo size={12} className="flex-shrink-0 mt-0.5" />
                <span>
                  A Lei 12.527/2011 (Art. 10, § 3º) <strong>veda exigir justificativa</strong> para pedidos
                  de acesso à informação. Este campo é meramente facultativo.
                </span>
              </p>
            </div>
          </div>
        </SectionCard>

        <SectionCard titulo="Seus dados serão usados" icone={<FiShield size={18} />}>
          <p className="text-xs text-slate-600">
            A LAI exige identificação do solicitante. Seu nome, CPF e contato cadastrados no portal serão
            vinculados a este pedido. Em hipótese alguma serão divulgados publicamente sem seu consentimento.
          </p>
          {cidadao && (
            <div className="mt-3 text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded p-3">
              <div><strong>Solicitante:</strong> {cidadao.nome}</div>
              {cidadao.cpf && <div><strong>CPF:</strong> {cidadao.cpf}</div>}
              <div><strong>E-mail:</strong> {cidadao.email}</div>
            </div>
          )}
        </SectionCard>

        <SectionCard titulo="Anexos (opcional)" subtitulo={`Documentos que ajudem no pedido. Até ${MAX_ANEXOS} arquivos.`} icone={<FiPaperclip size={18} />}>
          <AnexoUpload
            arquivos={anexos}
            onChange={setAnexos}
            extensoesPermitidas={EXT_ANEXOS}
            tamanhoMaxMB={10}
            maxArquivos={MAX_ANEXOS}
            legenda={`Até ${MAX_ANEXOS} arquivos · 10 MB cada · ${EXT_ANEXOS.join(', ').toUpperCase()}`}
          />
        </SectionCard>

        <div className="flex items-center justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/')}>Cancelar</Button>
          <Button type="submit" loading={enviando} disabled={assuntos.length === 0}>
            <FiSend size={14} /> Enviar pedido
          </Button>
        </div>
      </form>
    </PortalPage>
  );
}

function PainelSucesso({ numero, hash, onVer }: { numero: string; hash: string; onVer: () => void }) {
  const link = `${window.location.origin}/protocolo/${numero}/${hash}`;
  const [copiado, setCopiado] = useState(false);
  function copiar() {
    navigator.clipboard.writeText(link);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  return (
    <PainelResultado
      titulo="Pedido protocolado"
      descricao={<>Seu protocolo é <strong className="text-slate-900">{numero}</strong></>}
      acoes={<Button onClick={onVer}>Acompanhar pedido</Button>}
    >
      <p className="text-center text-xs text-slate-500">
        A prefeitura tem até <strong>20 dias úteis</strong> para responder, prorrogáveis por mais 10
        com justificativa.
      </p>

      <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
        <Label>Link público de acompanhamento</Label>
        <div className="flex items-center gap-2 mt-1">
          <Input value={link} readOnly className="text-xs flex-1" />
          <Button type="button" variant="outline" onClick={copiar}>
            <FiCopy size={14} /> {copiado ? 'Copiado!' : 'Copiar'}
          </Button>
        </div>
      </div>

      <Alert variante="info" className="mt-4">
        <FiAlertCircle size={12} className="inline mr-1" />
        Você receberá notificações por e-mail a cada movimentação. Se a resposta for negativa,
        você poderá recorrer em até 4 instâncias.
      </Alert>
    </PainelResultado>
  );
}
